// Custom hook for API calls
import { useState, useCallback } from 'react';
import {
  CreateStoryRequest,
  CreateStoryResponse,
  NextSceneResponse,
  RollDiceResponse,
  GameState,
  PromptEntry,
  CombatEncounter
} from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.details || error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

export function useGameApi() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createStory = useCallback(async (request: CreateStoryRequest): Promise<CreateStoryResponse> => {
    setLoading(true);
    setError(null);
    try {
      return await fetchApi<CreateStoryResponse>('/stories', {
        method: 'POST',
        body: JSON.stringify(request),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getStory = useCallback(async (sessionId: string): Promise<GameState> => {
    setLoading(true);
    setError(null);
    try {
      return await fetchApi<GameState>(`/stories/${sessionId}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const continueStory = useCallback(async (
    sessionId: string,
    choiceIndex: number,
    rollResult?: number,
    modifier?: number,
    statAbbrev?: string
  ): Promise<NextSceneResponse> => {
    setLoading(true);
    setError(null);
    try {
      return await fetchApi<NextSceneResponse>(`/stories/${sessionId}/scenes`, {
        method: 'POST',
        body: JSON.stringify({ choiceIndex, rollResult, modifier, statAbbrev }),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const rollDice = useCallback(async (
    sessionId: string,
    diceType: string,
    modifier?: number,
    actionType?: string
  ): Promise<RollDiceResponse> => {
    setLoading(true);
    setError(null);
    try {
      return await fetchApi<RollDiceResponse>(`/stories/${sessionId}/roll`, {
        method: 'POST',
        body: JSON.stringify({ diceType, modifier, actionType }),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const completeStory = useCallback(async (sessionId: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await fetchApi(`/stories/${sessionId}/complete`, {
        method: 'POST',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const abandonStory = useCallback(async (sessionId: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await fetchApi(`/stories/${sessionId}/abandon`, {
        method: 'POST',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getPromptLog = useCallback(async (sessionId: string): Promise<PromptEntry[]> => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchApi<{ prompts: PromptEntry[] }>(`/stories/${sessionId}/prompts`);
      return result.prompts;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getActiveCombat = useCallback(async (sessionId: string): Promise<CombatEncounter | null> => {
    setLoading(true);
    setError(null);
    try {
      return await fetchApi<CombatEncounter | null>(`/combat/${sessionId}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    createStory,
    getStory,
    continueStory,
    rollDice,
    completeStory,
    abandonStory,
    getPromptLog,
    getActiveCombat,
  };
}
